import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import formCss from '@lib/style/form.module.scss';
import { ButtonBase } from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { scheduleSettings } from '@stores/ScheduleSettings';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { TimeZoneDialog } from '../components/TimeZoneDialog/TimeZoneDialog';
import { PostFormStore, PublishWhen, usePostForm } from './PostFormStore';
import css from './PublishTimeField.module.scss';

const WHEN_OPTIONS: { value: PublishWhen; label: string }[] = [
  { value: 'at', label: 'Schedule' },
  { value: 'now', label: 'Post now' },
];

/** The button this page offers for a past time: the API treats a past time as "now" on every path. */
const passedAction = (form: PostFormStore): string =>
  form.mode.kind === 'create' ? 'Scheduling' : form.retryable ? 'Retry' : 'Saving';

/**
 * When the post goes out: a time picked in the schedule's zone, or "now",
 * sent as the clock at submit. The picker starts empty on create; a post may
 * be at most a year ahead (the API's rule).
 *
 * The picker runs on Luxon, the one adapter here that follows a zone: the
 * date-fns adapter parses typed text into the browser's zone. The store keeps
 * Unix ms; Luxon's `DateTime` exists only between this field and the picker.
 *
 * FIXME(publishing-picker-luxon): Luxon is a second date library carried for
 * this one field, a workaround for `AdapterDateFnsBase.isTimezoneCompatible
 * = false` in @mui/x-date-pickers 9.14. Acceptable once: the alternative was
 * a subclass of the date-fns adapter riding on its internals. Retire it,
 * with the `luxon` and `@types/luxon` dependencies, when MUI's date-fns
 * adapter (or a `TZDate` one) takes the `timezone` prop; the field then goes
 * back to date-fns with `scheduleSettings.inZone`.
 *
 * A zone change under a typed time keeps the instant, not the typed clock:
 * 6 PM typed in Bucharest reads 8 AM after a switch to Los Angeles, in the
 * field right above the link that changed it. Decided so for both create
 * and edit; the stored post on edit is a real moment either way.
 */
export const PublishTimeField = observer(function PublishTimeField({ now }: { now: Date }) {
  const form = usePostForm();
  const [zoneOpen, setZoneOpen] = useState(false);
  const readOnly = form.readOnly;
  const zone = scheduleSettings.timeZone;
  const value = form.publishAt === null ? null : DateTime.fromMillis(form.publishAt, { zone });
  const passed = form.when === 'at' && form.publishAt !== null && form.publishAt < now.getTime();

  return (
    <div className={formCss.field}>
      <span className={formCss.fieldLabel}>When</span>
      <div className={css.row}>
        <MultipleToggle
          options={WHEN_OPTIONS.map((option) => ({ ...option, disabled: readOnly }))}
          value={form.when}
          onChange={(when) => form.setWhen(when)}
        />
        {form.when === 'at' && (
          <div className={css.picker}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateTimePicker
                value={value}
                timezone={zone}
                onChange={(date) =>
                  form.setPublishAt(date && date.isValid ? date.toMillis() : null)
                }
                disabled={readOnly}
                // A stored time in the past is shown as it is on a post that can no longer change.
                disablePast={!readOnly}
                maxDateTime={DateTime.fromMillis(now.getTime(), { zone }).plus({ years: 1 })}
                slotProps={{
                  textField: {
                    className: formCss.textInput,
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        )}
      </div>
      <span className={formCss.fieldHint}>
        {form.when === 'now' ? (
          'Publishes as soon as you confirm.'
        ) : (
          // The zone and its link stay in every state: a time that "has passed" is often one typed for another zone.
          <>
            {passed &&
              !readOnly &&
              `This time has passed. ${passedAction(form)} publishes right away, or pick a new time. `}
            Times are in {scheduleSettings.timeZoneLabel(now.getTime())}.{' '}
            <ButtonBase className={css.changeZone} onClick={() => setZoneOpen(true)}>
              Change
            </ButtonBase>
          </>
        )}
      </span>
      <TimeZoneDialog open={zoneOpen} onClose={() => setZoneOpen(false)} source="compose" />
    </div>
  );
});
